/**
 * CalculationSummaryBar — souhrnný pruh nad seznamem termínů (4 dlaždice). Bez vybraného termínu ukazuje
 * agregát přes všechny termíny (počet, profit, marže, prodáno míst); s vybraným termínem jeho profit / náklad /
 * aktuální profit. Pouze Tailwind `phx-*` tokeny.
 */
import { useTranslation } from 'react-i18next';

import type { CalculationTermDto } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { Money } from '@/shared/ui';
import { formatOneDecimal, formatTermDate } from '../../../detail/serieFormat';
import type { AggregateMetrics, TermMetrics } from '../model/calc';

interface CalculationSummaryBarProps {
  selected: { term: CalculationTermDto; metrics: TermMetrics } | null;
  aggregate: AggregateMetrics;
}

const cellLabel = 'text-phx-2xs font-semibold uppercase tracking-wide text-phx-text-muted';
const bigValue = 'mt-px text-phx-h2 font-bold tabular-nums';

function signColor(value: number): string {
  return value >= 0 ? 'text-phx-success' : 'text-phx-danger';
}

export function CalculationSummaryBar({ selected, aggregate }: CalculationSummaryBarProps) {
  const { t } = useTranslation('venua-planner');

  const profit = selected ? selected.metrics.profit : aggregate.totalProfit;

  return (
    <div className="border-phx-border-2 bg-phx-surface rounded-phx shadow-phx-xs flex shrink-0 items-stretch overflow-hidden border">
      <div className="border-phx-border min-w-0 flex-[1.4] border-r px-3.5 py-2.5">
        <div className={cellLabel}>
          {selected ? t('calculation.summary.selectedTerm') : t('calculation.summary.allTerms')}
        </div>
        <div className="mt-0.5 truncate text-[15px] font-bold whitespace-nowrap">
          {selected
            ? formatTermDate(selected.term.dateFrom, selected.term.dateTo)
            : t('calculation.summary.termCount', { n: aggregate.termsCount })}
        </div>
      </div>

      <div className="border-phx-border min-w-0 flex-1 border-r px-3.5 py-2.5">
        <div className={cellLabel}>{t('calculation.summary.profitFullCapacity')}</div>
        <Money value={profit} decimals={0} className={cn(bigValue, 'block', signColor(profit))} />
      </div>

      <div className="border-phx-border min-w-0 flex-[0.9] border-r px-3.5 py-2.5">
        {selected ? (
          <>
            <div className={cellLabel}>{t('calculation.summary.costFullCapacity')}</div>
            <Money
              value={selected.metrics.costFull}
              decimals={0}
              className={cn(bigValue, 'block')}
            />
          </>
        ) : (
          <>
            <div className={cellLabel}>{t('calculation.summary.margin')}</div>
            <div className={cn(bigValue, signColor(aggregate.totalProfit))}>
              {formatOneDecimal(aggregate.marginPercent)} %
            </div>
          </>
        )}
      </div>

      <div className="min-w-0 flex-1 px-3.5 py-2.5">
        {selected ? (
          <>
            <div className={cellLabel}>{t('calculation.summary.currentProfit')}</div>
            <Money
              value={selected.metrics.profitNow}
              decimals={0}
              className={cn(bigValue, 'block', signColor(selected.metrics.profitNow))}
            />
          </>
        ) : (
          <>
            <div className={cellLabel}>{t('calculation.summary.soldSeats')}</div>
            <div className={cn(bigValue, 'tabular-nums')}>
              {aggregate.totalSold} / {aggregate.totalCapacity}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
